<script setup lang="ts">
  import { Head, router, useForm } from '@inertiajs/vue3';
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import Vapor from 'laravel-vapor';
  import axios from 'axios';
  import { Vue3Lottie } from 'vue3-lottie';

  import { ArrowLongLeftIcon, EnvelopeIcon } from '~/icons';
  import { InputError, FileInputShowcase, DemoDocumentHolder } from '~/components';
  import { Minimal } from '~/layouts';
  import { FormErrors, Import, MuResource, MauResource } from '~/types';
  import { Scan, LoggedIn } from '~/lottie';
  import { LocaleWithRegion, useLocalization } from '~/utils/localization';
  import { handleCatchError } from '~/utils';

  const { t, setLocale } = useLocalization();

  interface Props {
    email?: string;
    showcaseAiAnalysisImport?: Import | null;
    mu?: MuResource | null;
    mau?: MauResource | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    firstName: '',
    lastName: '',
    email: '',
    showcaseAiAnalysisImport: null,
    mu: null,
    mau: null,
  });

  const urlSearchParams = new URLSearchParams(window.location.search);

  const form = useForm({
    email: props.email ?? '',
    pdf_uuid: '',
    pdf_file_name: '',
    signup_type: 'register_showcase_ai_analysis',
  });

  const showNoFileUploadedError = ref(false);

  // Look for the lang= query parameter in the URL
  const queryLocale = urlSearchParams.get('lang');
  if (queryLocale) {
    setLocale(queryLocale as LocaleWithRegion);
  }

  const submit = async () => {
    // If no file was uploaded, we need to display an error message
    if (!form.pdf_uuid) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error: FormErrors<any> = { file: [t('auth.registerShowcase.noFileUploadedError')] };
      fileInputShowcase.value?.setError(error);
      return;
    }

    await checkEmail();

    if (validEmail.value) {
      form
        .transform((data) => ({
          ...data,
          utm_source: urlSearchParams.get('utm_source'),
          utm_medium: urlSearchParams.get('utm_medium'),
          utm_campaign: urlSearchParams.get('utm_campaign'),
          utm_term: urlSearchParams.get('utm_term'),
          utm_content: urlSearchParams.get('utm_content'),
          mtm_campaign: urlSearchParams.get('mtm_campaign'),
          mtm_kwd: urlSearchParams.get('mtm_kwd'),
          mtm_source: urlSearchParams.get('mtm_source'),
          mtm_medium: urlSearchParams.get('mtm_medium'),
          mtm_content: urlSearchParams.get('mtm_content'),
          mtm_cid: urlSearchParams.get('mtm_cid'),
          mtm_group: urlSearchParams.get('mtm_group'),
          mtm_placement: urlSearchParams.get('mtm_placement'),
          gclid: urlSearchParams.get('gclid'),
          gbraid: urlSearchParams.get('gbraid'),
          wbraid: urlSearchParams.get('wbraid'),
          li_fat_id: urlSearchParams.get('li_fat_id'),
          muid:
            urlSearchParams.get('muid') ??
            document.cookie
              .split('; ')
              .find((row) => row.startsWith('_pk_id'))
              ?.split('=')[1]
              .split('.')[0],
          fprom_ref:
            urlSearchParams.get('fprom_ref') ??
            document.cookie
              .split('; ')
              .find((row) => row.startsWith('_fprom_ref'))
              ?.split('=')[1]
              .split('.')[0],
          fprom_tid:
            urlSearchParams.get('fprom_tid') ??
            document.cookie
              .split('; ')
              .find((row) => row.startsWith('_fprom_tid'))
              ?.split('=')[1]
              .split('.')[0],
        }))
        .post(route('register'), {
          onSuccess: () => triggerSuccess(),
          // onFinish: () => form.reset("password", "password_confirmation"),
        });
    }
  };

  const triggerSuccess = () => {
    const message = 'success';
    window.parent.postMessage(message, '*');
  };

  const triggerRedirect = () => {
    const message = 'redirectToApp';
    window.parent.postMessage(message, '*');
  };

  const pdfUuid = ref<string>();
  const pdfFileName = ref<string>();

  const handleFileUploadSuccess = (file_uuid: string, file_name: string) => {
    pdfUuid.value = file_uuid;
    pdfFileName.value = file_name;
    form.pdf_uuid = file_uuid;
    form.pdf_file_name = file_name;
  };

  interface DemoFile {
    name: string;
    url: string;
  }

  const demoDocuments = computed<DemoFile[]>(() => {
    return [
      {
        name: t('auth.registerShowcase.softwareAgreement'),
        url: Vapor.asset('sample-contracts/' + t('auth.registerShowcase.softwareAgreementFileName')),
      },
      {
        name: t('auth.registerShowcase.nonDisclosureAgreement'),
        url: Vapor.asset('sample-contracts/' + t('auth.registerShowcase.nonDisclosureAgreementFileName')),
      },
      {
        name: t('auth.registerShowcase.rentalAgreement'),
        url: Vapor.asset('sample-contracts/' + t('auth.registerShowcase.rentalAgreementFileName')),
      },
      {
        name: t('auth.registerShowcase.employmentContract'),
        url: Vapor.asset('sample-contracts/' + t('auth.registerShowcase.employmentContractFileName')),
      },
    ];
  });

  const fileInputShowcase = ref();

  const handleSetSampleFile = (pdfUrl: string) => {
    fileInputShowcase.value?.setFile(pdfUrl);
  };

  const sendHeight = () => {
    const message = 'height:' + document.body.scrollHeight;
    window.parent.postMessage(message, '*');
  };

  onMounted(() => {
    window.addEventListener('resize', sendHeight);
    sendHeight();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', sendHeight);
  });

  const isVerified = computed(() => {
    return !!props?.mu?.email_verified_at || !!props?.mau?.user?.email_verified_at;
  });

  const resend = () => form.post(route('verification.send'));

  router.on('navigate', () => {
    sendHeight();
  });

  router.on('success', () => {
    sendHeight();
  });

  const logout = () => {
    form.post(route('logout'), {
      onSuccess: () => {
        router.visit(route('register-showcase'));
      },
    });
  };

  const isCheckingEmail = ref(false);
  const validEmail = ref<boolean | null>(null);
  const checkEmail = async () => {
    if (!isCheckingEmail.value) {
      isCheckingEmail.value = true;
      validEmail.value = null;
      try {
        await axios.post(route('api.email-check'), {
          email: form.email,
        });
        isCheckingEmail.value = false;
        validEmail.value = true;
      } catch (err: unknown) {
        handleCatchError(err, 'Checking email in register showcase');
        isCheckingEmail.value = false;
        validEmail.value = false;
      }
    }
  };
</script>

<template>
  <Minimal>
    <Head :title="$t('auth.registerShowcase.demoAiAnalysis')" />
    <div
      v-if="props.mu || props.mau"
      class="my-4"
    >
      <div
        v-if="isVerified"
        class="flex flex-col items-center justify-center"
      >
        <Vue3Lottie
          :animation-data="LoggedIn"
          class="max-w-48"
          aria-hidden="true"
        />
        <div class="max-w-lg px-4 pb-4 text-base text-center">
          <h3 class="mb-2 text-lg font-semibold text-indigo-500">
            {{ $t('auth.registerShowcase.loggedInTitle') }}
          </h3>
          <div>{{ $t('auth.registerShowcase.loggedInHint') }}</div>
          <button
            type="button"
            class="inline-block mt-4 text-base intro-button btn-primary"
            @click="triggerRedirect"
          >
            {{ $t('auth.registerShowcase.loggedInHint2') }}
          </button>
        </div>
      </div>
      <div
        v-else-if="showcaseAiAnalysisImport?.uuid"
        class="flex flex-col items-center justify-center"
      >
        <Vue3Lottie
          :animation-data="Scan"
          class="max-w-48"
          aria-hidden="true"
        />
        <div class="max-w-xl px-4 pb-4 text-base text-center">
          <h3 class="mb-4 text-lg font-semibold text-indigo-500">
            {{ $t('auth.registerShowcase.importActiveTitle') }}
          </h3>
          {{ $t('auth.registerShowcase.importActiveConfirmEmail') }}
          <div
            class="flex flex-col items-center gap-3 p-6 mt-6 font-semibold border rounded-md shadow-lg border-white/75 bg-linear-to-br from-white to-indigo-100"
          >
            <div
              class="flex items-center justify-center text-white rounded-full bg-linear-to-br from-indigo-500 to-indigo-800 w-14 h-14"
            >
              <EnvelopeIcon class="w-8 h-8" />
            </div>
            <div class="max-w-md">
              {{ $t('auth.registerShowcase.importActiveConfirmEmail2') }}
            </div>
            <form @submit.prevent="resend">
              <div class="flex items-center justify-center gap-3">
                <button
                  type="button"
                  class="flex items-center gap-2 text-base text-indigo-300 btn-plain hover:text-indigo-700 focus:ring-indigo-200"
                  @click="logout"
                >
                  <ArrowLongLeftIcon class="w-4 h-4" />
                  {{ $t('auth.verifyEmail.startOver') }}
                </button>
                <button
                  type="submit"
                  class="text-base text-indigo-500 btn-plain hover:text-indigo-700 hover:bg-indigo-100 focus:ring-indigo-200"
                  :disabled="form.processing"
                >
                  {{ $t('auth.verifyEmail.button') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-else>
        <div
          class="flex flex-col items-center gap-3 p-6 font-semibold border rounded-md shadow-lg border-white/75 bg-linear-to-br from-white to-indigo-100"
        >
          <div
            class="flex items-center justify-center text-white rounded-full bg-linear-to-br from-indigo-500 to-indigo-800 w-14 h-14"
          >
            <EnvelopeIcon class="w-8 h-8" />
          </div>
          {{ $t('auth.registerShowcase.confirmEmailFromRegularSignup') }}
          <form @submit.prevent="resend">
            <div class="flex items-center justify-center">
              <button
                type="submit"
                class="w-full text-base text-indigo-500 btn-plain hover:text-indigo-700 focus:ring-indigo-200"
                :disabled="form.processing"
              >
                {{ $t('auth.verifyEmail.button') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div
      v-else
      class="@container grid gap-6 lg:grid-cols-3 my-4"
    >
      <DemoDocumentHolder
        :demo-documents="demoDocuments"
        @set-sample-file="handleSetSampleFile"
      />
      <form
        class="flex flex-col lg:col-span-2"
        @submit.prevent="submit"
      >
        <div class="grow">
          <FileInputShowcase
            ref="fileInputShowcase"
            class="text-sm"
            :placeholder="$t('auth.registerShowcase.dropPdfFile')"
            accept=".pdf,.PDF"
            :multiple="false"
            allowed-extensions="/\.pdf$/i"
            :upload-callback="handleFileUploadSuccess"
            @resize="sendHeight"
          />
          <div
            v-if="showNoFileUploadedError"
            class="p-2 my-2 text-xs text-red-500 bg-red-100 rounded-md"
          >
            {{ $t('auth.registerShowcase.noFileUploadedError') }}
          </div>
        </div>

        <div>
          <div class="mt-4">
            <label
              class="block text-sm font-medium leading-6 text-gray-900"
              for="email"
            >
              {{ $t('auth.login.email') }}
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="block w-full mt-1 input-plain"
              required
              autocomplete="username"
              :placeholder="$t('auth.login.emailPlaceholder') + '…'"
            />
            <InputError
              v-if="form.errors?.email"
              class="mt-2"
              :message="form.errors.email"
            />
            <InputError
              v-if="validEmail !== null && !validEmail"
              class="mt-2"
              :message="$t('auth.register.emailNotAllowed')"
            />
          </div>

          <div class="block mt-6">
            <button
              type="submit"
              class="w-full py-4 text-base btn-primary intro-button"
              :disabled="form.processing"
            >
              {{ $t('auth.registerShowcase.startAnalysis') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </Minimal>
</template>
